import { type ChangeEvent } from "react";
import NextLink from "next/link";
import { useRouter } from "next/compat/router";
import {
  Loader,
  Box,
  Select,
  Button,
  Heading,
  IconFa,
} from "@cruk/cruk-react-components";
import { faTrophy } from "@fortawesome/free-solid-svg-icons";

import { useTracking } from "@fwa/src/hooks/useTracking";
import { useSwrGet } from "@fwa/src/hooks/useSwrGet";
import { fwsUrSuperTeamPageFundraisingPages } from "@fwa/src/services/superTeamService";
import { queryAsString } from "@fwa/src/utils/urlUtils";
import { isPast, isToday } from "@fwa/src/utils/timeUtils";

import { MemberFundraisingPageDonate } from "@fwa/src/components/MemberFundraisingPageDonate";

import sharedStyles from "@fwa/src/components/styles.module.css";

import {
  type SuperTeamPageType,
  type SuperTeamMemberSortType,
  type FundraisingPageType,
  type FwsResponseData,
} from "@fwa/src/types";

const joinTeamSVG = "/assets/images/img/join-team.svg";

export const SuperTeamFundraisingPagesOverview = ({
  page,
}: {
  page: SuperTeamPageType;
}) => {
  const { pageStatus, closeDate } = page;
  const isPageClosed: boolean =
    pageStatus === "closed" ||
    (closeDate &&
      (isToday(new Date(closeDate)) || isPast(new Date(closeDate)))) ||
    false;
  const MAX_ITEMS = isPageClosed ? 6 : 5;
  const { trackEventGtm, trackError } = useTracking();
  const router = useRouter();
  const { sort: sortQuery } = router?.query || {};
  const sort = (queryAsString(sortQuery) ||
    "amount") as SuperTeamMemberSortType;
  const url = page
    ? fwsUrSuperTeamPageFundraisingPages({
        teamId: page.uniqueId,
        currentPage: 1,
        itemsPerPage: MAX_ITEMS,
        sort,
      })
    : null;
  const { data, error, totalItems } = useSwrGet<
    FwsResponseData<FundraisingPageType>
  >(url, {});
  const fundraisingPages = data?.results || [];
  const showMoreLink = totalItems > MAX_ITEMS;

  const setSortInQueryString = (sortValue: string) => {
    router
      ?.replace(
        {
          pathname: router.pathname,
          query: { ...router.query, sort: sortValue },
        },
        undefined,
        { shallow: true },
      )
      .catch((err: Error) => {
        trackError(err, {
          component: "SuperTeamFundraisingPagesOverview",
          function: "setSortInQueryString",
        });
      });
  };

  const handleFilterChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const sortValue = e.target.value as SuperTeamMemberSortType;
    if (!sortValue) return;
    setSortInQueryString(sortValue);
  };

  return (
    <>
      {/* Loading State */}
      {!error && !data ? (
        <Box backgroundColor="background-light" padding="s">
          <Loader />
        </Box>
      ) : null}

      {/* Data State */}
      {data ? (
        <Box
          backgroundColor="background-light"
          padding="s"
          data-component="super-team-pages-overview"
        >
          <Heading h2 textSize="xl">
            Giving Pages
          </Heading>
          <Box marginBottom="m">
            <NextLink
              href={`/unite${page.url.slice(
                page.url.lastIndexOf("/"),
              )}/leaderboardpages`}
              data-cta-type="link-super-team-leaderboard"
            >
              <Button appearance="secondary" as="span">
                <IconFa faIcon={faTrophy} />
                Show leaderboard
              </Button>
            </NextLink>
          </Box>
          {!!fundraisingPages.length && (
            <Box>
              <div style={{ maxWidth: "26em" }}>
                <Select
                  required
                  value={sort}
                  label="Sort Giving Pages by"
                  onChange={handleFilterChange}
                  aria-controls="memberList"
                  hideRequiredIndicationInLabel
                >
                  <option value="amount">amount raised</option>
                  <option value="alpha">A-Z</option>
                  <option value="recent">recently joined</option>
                </Select>
              </div>
            </Box>
          )}
          <ul
            className={sharedStyles["member-list"]}
            id="memberList"
            aria-live="assertive"
            data-component="super-team-pages-member-list"
          >
            {isPageClosed ? null : (
              <li
                style={{
                  display: "flex",
                  minHeight: "8em",
                  width: "100%",
                  borderRadius: "var(--btn-border-radius, 0)",
                  border: "dashed 2px var(--clr-cruk-grey-300, #c6c6c6)",
                  padding: "var(--spacing-xs, 1rem)",
                  flexDirection: "row",
                  flexWrap: "wrap",
                  alignItems: "flex-start",
                }}
              >
                <div
                  style={{
                    height: "5em",
                    width: "5em",
                    minWidth: "5em",
                    objectFit: "fill",
                  }}
                >
                  <img src={joinTeamSVG} alt="join team" />
                </div>

                <Box marginTop="xs" marginHorizontal="auto">
                  <NextLink
                    href={`/unite${page.url.slice(
                      page.url.lastIndexOf("/"),
                    )}/join`}
                    onClick={() => {
                      trackEventGtm({ event: "join_superteam_cta" });
                    }}
                    data-cta-type="create-page"
                  >
                    <Button appearance="primary" as="span">
                      Create a Giving Page
                    </Button>
                  </NextLink>
                </Box>
              </li>
            )}
            {!error &&
              fundraisingPages.map((fundraisingPage: FundraisingPageType) => (
                <li key={fundraisingPage.uniqueId}>
                  <MemberFundraisingPageDonate
                    fundraisingPage={fundraisingPage}
                  />
                </li>
              ))}
          </ul>

          {showMoreLink && (
            <Box marginVertical="s">
              <NextLink
                href={`/unite${page?.url?.slice(
                  page.url.lastIndexOf("/"),
                )}/pages`}
                data-cta-type="link-all-super-teams"
              >
                <Button
                  appearance="secondary"
                  as="span"
                >{`See all Giving Pages (${totalItems})`}</Button>
              </NextLink>
            </Box>
          )}
        </Box>
      ) : null}
    </>
  );
};

export default SuperTeamFundraisingPagesOverview;
